import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";
import Placeholder from "@/components/Placeholder";

/** Cinematic gold particle field — placeholder for the owner's hero video. */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = "";
    const count = 32;
    for (let i = 0; i < count; i++) {
      const p = document.createElement("span");
      p.className = "particle";
      const size = 2 + Math.random() * 5;
      p.style.width = `${size}px`;
      p.style.height = `${size}px`;
      p.style.left = `${Math.random() * 100}%`;
      p.style.bottom = `-${Math.random() * 30}px`;
      p.style.animationDuration = `${8 + Math.random() * 10}s`;
      p.style.animationDelay = `${Math.random() * 8}s`;
      el.appendChild(p);
    }
  }, []);
  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
}

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden hero-radial flex items-center"
    >
      {/* Background video placeholder */}
      <div className="absolute inset-0">
        <Placeholder
          testId="hero-video-placeholder"
          className="w-full h-full !rounded-none border-0"
          minHeight="100%"
          rounded="0"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,6,6,0.55) 0%, rgba(6,6,6,0.35) 40%, rgba(6,6,6,0.9) 100%)",
          }}
        />
        <ParticleField />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full grid md:grid-cols-2 gap-10 items-center pt-28 pb-24">
        <div className="text-reveal">
          <div className="eyebrow mb-6">
            <Sparkles size={14} /> Elite Eventz Presents
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] font-semibold">
            Creating{" "}
            <span className="gold-text-gradient italic">Extraordinary</span>
            <br />
            Experiences
          </h1>

          <p className="mt-7 text-lg text-neutral-300 max-w-xl leading-relaxed">
            {/* Typography fix: use a single short dash instead of a double-dash pause. */}
            From cultural festivals to concerts, sports tournaments and
            unforgettable community celebrations – we craft nights the city
            remembers.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#legacy"
              data-testid="hero-cta-explore"
              className="btn-outline-gold"
            >
              Explore Events
            </a>
            <a
              href="#royale-raas"
              data-testid="hero-cta-royale"
              className="btn-gold"
            >
              Royale Raas <span aria-hidden>→</span>
            </a>
          </div>
        </div>

        <div className="relative hidden md:block">
          {/* Right side visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            className="relative aspect-[4/5] w-full max-w-md ml-auto glass rounded-3xl overflow-hidden bg-black"
          >
            <img
              data-testid="hero-side-image"
              src="/hero/hero-royale-raas.jpeg"
              alt="Royale Raas — Pune Dandiya Mahotsav 2026"
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover object-center"
              draggable={false}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)",
              }}
            />
            <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl p-4">
              <p className="text-xs tracking-[0.3em] text-[var(--ee-gold)] uppercase">
                Next
              </p>
              <p className="font-display text-2xl mt-1">Royale Raas</p>
              <p className="text-xs text-neutral-400 mt-1">
                16 – 18 October 2026
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-neutral-500"
      >
        <div className="h-8 w-5 rounded-full border border-[var(--ee-gold)]/40 flex items-start justify-center pt-1.5">
          <div className="scroll-dot" />
        </div>
        <ChevronDown size={14} className="text-[var(--ee-gold)]/70" />
      </div>
    </section>
  );
}
